import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
} from '@mui/material';
import { PrimaryButton, SecondaryButton } from '../buttons/buttons';
import { useTranslation } from 'react-i18next';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export interface SubscriptionRequiredDialogProps {
  open: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  subscriptionType?: 'autoSearch' | 'autoNego' | 'mcp' | 'general';
}

const SubscriptionRequiredDialog: React.FC<SubscriptionRequiredDialogProps> = ({
  open,
  onClose,
  onOpenSettings,
  subscriptionType = 'general',
}) => {
  const { t } = useTranslation();

  const getSubscriptionName = () => {
    switch (subscriptionType) {
      case 'autoSearch':
        return t('Auto Search');
      case 'autoNego':
        return t('Auto Negotiation');
      case 'mcp':
        return t('MCP');
      default:
        return t('General');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.white',
          borderRadius: 2,
          padding: 2,
          border: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(30, 30, 30, 0.75)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningAmberIcon sx={{ fontSize: 40, color: 'text.warningOrange' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.darkGrey' }}>
            {t('Subscription Required')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
          {t('This feature requires an active {{subscriptionName}} subscription.', {
            subscriptionName: getSubscriptionName(),
          })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('Would you like to view subscription options and manage your plan?')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SecondaryButton fullWidth onClick={onClose}>
          {t('Cancel')}
        </SecondaryButton>
        <PrimaryButton fullWidth onClick={onOpenSettings}>
          {t('View Subscriptions')}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionRequiredDialog;
